import { useEffect, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import {
  EnvironmentNamingConvention,
  ScaffolderEnvironment,
  beehiveScaffolderEnvironmentsApiRef,
} from '../../apis/scaffolderEnvironments';

const equivalentEnvironmentGroups = [
  ['shared'],
  ['dev', 'development'],
  ['test'],
  ['stg', 'staging'],
  ['prod', 'production'],
];

function getDefaultConvention(conventions: EnvironmentNamingConvention[]) {
  return conventions.find(convention => convention.default) ?? conventions[0];
}

function findConventionForEnvironment(
  conventions: EnvironmentNamingConvention[],
  environmentId: string | undefined,
) {
  if (!environmentId) {
    return undefined;
  }

  const matches = conventions.filter(convention =>
    convention.environments.some(
      environment => environment.id === environmentId,
    ),
  );

  return matches.find(convention => convention.default) ?? matches[0];
}

function findEquivalentEnvironmentId(
  environmentId: string,
  environments: ScaffolderEnvironment[],
) {
  const group = equivalentEnvironmentGroups.find(items =>
    items.includes(environmentId),
  );

  if (!group) {
    return undefined;
  }

  return group.find(item =>
    environments.some(environment => environment.id === item),
  );
}

export function EnvironmentPicker(
  props: FieldExtensionComponentProps<string | undefined>,
) {
  const {
    disabled,
    formData,
    idSchema,
    onChange,
    rawErrors,
    readonly,
    required,
    schema,
  } = props;
  const environmentsApi = useApi(beehiveScaffolderEnvironmentsApiRef);
  const [conventions, setConventions] = useState<EnvironmentNamingConvention[]>(
    [],
  );
  const [selectedConventionId, setSelectedConventionId] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let mounted = true;

    environmentsApi
      .listEnvironmentNamingConventions()
      .then(items => {
        if (!mounted) {
          return;
        }

        const selectedConvention = getDefaultConvention(items);

        setConventions(items);
        setSelectedConventionId(selectedConvention?.id);
        setError(undefined);
      })
      .catch(e => {
        if (!mounted) {
          return;
        }

        setConventions([]);
        setSelectedConventionId(undefined);
        setError(
          e instanceof Error ? e.message : 'Failed to load environments',
        );
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [environmentsApi]);

  const selectedConvention = conventions.find(
    convention => convention.id === selectedConventionId,
  );

  useEffect(() => {
    if (!formData || conventions.length === 0) {
      return;
    }

    if (
      selectedConvention?.environments.some(
        environment => environment.id === formData,
      )
    ) {
      return;
    }

    const inferredConvention = findConventionForEnvironment(
      conventions,
      formData,
    );

    if (inferredConvention && inferredConvention.id !== selectedConventionId) {
      setSelectedConventionId(inferredConvention.id);
    }
  }, [conventions, formData, selectedConvention, selectedConventionId]);

  useEffect(() => {
    if (
      selectedConvention?.environments.length === 1 &&
      formData !== selectedConvention.environments[0].id
    ) {
      onChange(selectedConvention.environments[0].id);
    }
  }, [formData, onChange, selectedConvention]);

  const onConventionChange = (conventionId: string) => {
    const convention = conventions.find(item => item.id === conventionId);
    setSelectedConventionId(conventionId);

    if (!convention) {
      return;
    }

    if (formData) {
      const currentValueIsValid = convention.environments.some(
        environment => environment.id === formData,
      );

      if (currentValueIsValid) {
        return;
      }

      onChange(findEquivalentEnvironmentId(formData, convention.environments));
      return;
    }

    if (convention.environments.length === 1) {
      onChange(convention.environments[0].id);
    }
  };

  const hasError = Boolean(error) || Boolean(rawErrors?.length);
  const helperText = error ?? rawErrors?.[0] ?? schema.description;
  const label = schema.title ?? 'Environment';
  const conventionValue = conventions.some(
    convention => convention.id === selectedConventionId,
  )
    ? selectedConventionId
    : '';
  const conventionDisabled =
    disabled || readonly || loading || conventions.length === 0;
  const fieldDisabled =
    disabled ||
    readonly ||
    loading ||
    !selectedConvention ||
    selectedConvention.environments.length === 0;
  const selectValue = selectedConvention?.environments.some(
    environment => environment.id === formData,
  )
    ? formData
    : '';

  return (
    <>
      <FormControl
        fullWidth
        margin="normal"
        error={Boolean(error)}
        disabled={conventionDisabled}
      >
        <InputLabel id={`${idSchema.$id}-convention-label`}>
          Environment naming convention
        </InputLabel>
        <Select
          labelId={`${idSchema.$id}-convention-label`}
          id={`${idSchema.$id}-convention`}
          MenuProps={{ disablePortal: true }}
          value={conventionValue}
          onChange={event => onConventionChange(event.target.value as string)}
        >
          {conventions.map(convention => (
            <MenuItem key={convention.id} value={convention.id}>
              {convention.name}
            </MenuItem>
          ))}
        </Select>
        {loading && (
          <FormHelperText component="div">
            <CircularProgress size={14} /> Loading environments…
          </FormHelperText>
        )}
        {!loading && conventions.length === 0 && !error && (
          <FormHelperText>
            No environment naming conventions are configured.
          </FormHelperText>
        )}
        {selectedConvention?.description && (
          <FormHelperText>{selectedConvention.description}</FormHelperText>
        )}
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>

      <FormControl
        fullWidth
        margin="normal"
        required={required}
        error={hasError}
        disabled={fieldDisabled}
      >
        <InputLabel id={`${idSchema.$id}-label`}>{label}</InputLabel>
        <Select
          labelId={`${idSchema.$id}-label`}
          id={idSchema.$id}
          MenuProps={{ disablePortal: true }}
          value={selectValue}
          onChange={event => onChange(event.target.value as string)}
        >
          {selectedConvention?.environments.map(environment => (
            <MenuItem key={environment.id} value={environment.id}>
              {environment.name}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
        {formData && (
          <Typography variant="caption" color="textSecondary">
            Selected environment: {formData}
          </Typography>
        )}
      </FormControl>
    </>
  );
}

export const EnvironmentPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string | undefined>({
    name: 'EnvironmentPicker',
    component: EnvironmentPicker,
    schema: {
      returnValue: {
        type: 'string',
      },
    },
  }),
);
